// Bot-Filter fuer das Umami-Tracking (SEO-Audit Stufe D16).
//
// Umami verwirft bekannte Crawler selbst anhand des User-Agents. Uebrig bleiben
// automatisierte Browser aus Rechenzentren (AWS, Google Cloud, Huawei Cloud),
// die sich als normales Chrome ausgeben: ein Aufruf der Startseite, keine
// Herkunft, keine Bedienung. Sie machten rund 30 Prozent der Besucher aus.
//
// Der Filter haengt sich ueber `data-before-send` in den Tracker:
//   1. navigator.webdriver (Selenium, Puppeteer, Playwright): nie zaehlen.
//   2. Sonst erst zaehlen, wenn eine echte Bedienung erfolgt ist (Maus,
//      Mausrad, Taste, Touch; nur isTrusted-Ereignisse). Bis dahin werden
//      Aufrufe mit ihrem Zeitpunkt gemerkt und danach nachgesendet, damit die
//      erste Seite nicht verloren geht und zur richtigen Zeit gebucht wird
//      (Umami /api/send akzeptiert `timestamp` in Sekunden).
// Folge: Wer eine Seite oeffnet und ohne jede Bedienung wieder schliesst,
// wird nicht gezaehlt. Das betrifft praktisch nur Bots.
//
// Diese Datei existiert in hub (src/lib) und website (lib). Abweichung seit
// 20.10.2026: Nur der Hub entfernt den Newsletter-Klickparameter (siehe
// unten). Die Website bekommt ihn nie, dort ist das ueberfluessig.
//
// Datenschutz: Direkte Hub-Links aus Newslettern tragen ?nlk=<Empfaenger-ID>.
// <Signatur> (lib/newsletter-link.ts). Die Seite entfernt ihn aus der Adresse,
// aber erst nach dem ersten Umami-Aufruf. Dieser Filter nimmt ihn deshalb aus
// url und referrer JEDES Aufrufs, auch aus gemerkten, bevor etwas an Umami geht.

export const UMAMI_FILTER_NAME = '__umamiBotFilter'

type Payload = Record<string, unknown>
type Filter = (type: string, payload: Payload) => Payload | false

interface UmamiFenster {
  __umamiBotFilter?: Filter
  umami?: { track: (payload: Payload) => unknown }
}

// Bewusst ohne 'scroll': Skripte, Anker-Links und die Scroll-Wiederherstellung
// erzeugen ebenfalls vertrauenswuerdige Scroll-Ereignisse. Echtes Scrollen loest
// vorher wheel, touchstart, keydown oder pointerdown (Scrollbalken) aus.
const EREIGNISSE = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'] as const
const MAX_WARTEND = 10

const KLICK_PARAM = 'nlk'

/**
 * Entfernt den Klickparameter aus einer Adresse (absolut oder nur Pfad),
 * alles andere bleibt in Originalschreibweise. Bewusst ohne URL-Neuaufbau:
 * Umami bekommt teils nur Pfad+Query, und URL.toString() wuerde die Query neu
 * kodieren. Prueft auf den Parameternamen, nicht auf eine Teilzeichenkette.
 */
// Auch kodiert (Login-Seite: ?next=%2Fkalender%2Fx%3Fnlk%3D...).
const KLICK_PARAM_MUSTER = /nlk(=|%3D)/i

function ohneKlickParam(wert: unknown): unknown {
  if (typeof wert !== 'string' || !KLICK_PARAM_MUSTER.test(wert)) return wert
  const rautePos = wert.indexOf('#')
  const vorne = rautePos >= 0 ? wert.slice(0, rautePos) : wert
  const raute = rautePos >= 0 ? wert.slice(rautePos) : ''
  const q = vorne.indexOf('?')
  if (q < 0) return wert
  const teile = vorne.slice(q + 1).split('&')
    .filter(t => t !== '' && t.split('=')[0] !== KLICK_PARAM)
    .map(t => {
      // Das Login-Ziel traegt den Parameter kodiert in sich.
      const [name, ...rest] = t.split('=')
      if (name !== 'next' || rest.length === 0) return t
      try {
        const innen = ohneKlickParam(decodeURIComponent(rest.join('=')))
        return `next=${encodeURIComponent(String(innen))}`
      } catch {
        return t
      }
    })
  return `${vorne.slice(0, q)}${teile.length > 0 ? `?${teile.join('&')}` : ''}${raute}`
}

function bereinigt(payload: Payload): Payload {
  if (!payload || typeof payload !== 'object') return payload
  return { ...payload, url: ohneKlickParam(payload.url), referrer: ohneKlickParam(payload.referrer) }
}

export function installiereUmamiBotFilter(): void {
  if (typeof window === 'undefined') return
  const w = window as unknown as UmamiFenster
  if (w.__umamiBotFilter) return

  const automatisiert = navigator.webdriver === true
  let mensch = false
  const wartend: Payload[] = []

  const freigeben = (e: Event) => {
    if (mensch || !e.isTrusted) return
    mensch = true
    for (const name of EREIGNISSE) window.removeEventListener(name, freigeben, true)
    const offen = wartend.splice(0)
    for (const p of offen) w.umami?.track(p)
  }

  if (!automatisiert) {
    for (const name of EREIGNISSE) window.addEventListener(name, freigeben, { capture: true, passive: true })
  }

  w.__umamiBotFilter = (type, roh) => {
    if (automatisiert) return false
    const payload = bereinigt(roh)
    if (mensch) return payload
    if (type === 'event' && wartend.length < MAX_WARTEND) {
      wartend.push({ ...payload, timestamp: Math.floor(Date.now() / 1000) })
    }
    return false
  }
}
